import { useEffect, useRef, useState } from 'react';

import { apiV1Base } from '../lib/apiConfig';
export type Position = {
  symbol: string;
  quantity: number;
  side: 'long' | 'short';
  avg_entry_price: number;
  current_price: number;
  market_value: number;
  cost_basis: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  change_today: number;
  asset_class: string;
  exchange: string;
};

export type Account = {
  id: string;
  display_name: string;
  account_value: number;
  equity: number;
  buying_power: number;
  daily_pnl: number;
  total_pnl: number;
  open_positions: number;
  positions: Position[];
  trading_allowed: boolean;
  status: string;
  goal_mode?: string;
  regime?: string;
  recovery_status?: any;
  trading_style?: string;
};

const POLL_MS = 30_000;
const STORAGE_KEY = 'adversyn:selected-account-id';

function apiBase(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  return apiV1Base();
}

function loadStoredId(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}
function storeId(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

function pickDefault(accounts: Account[], preferredId?: string | null): Account | null {
  if (!accounts || accounts.length === 0) return null;
  if (preferredId) {
    const found = accounts.find((a) => a.id === preferredId);
    if (found) return found;
  }
  const named = accounts.find(
    (a) => /day.?trad/i.test(a.display_name || '') || /day.?trad/i.test(a.id || ''),
  );
  if (named) return named;
  const target = 24000;
  let best = accounts[0];
  let bestDiff = Math.abs((best.account_value || best.equity || 0) - target);
  for (const a of accounts) {
    const v = a.account_value || a.equity || 0;
    const d = Math.abs(v - target);
    if (d < bestDiff) {
      best = a;
      bestDiff = d;
    }
  }
  return best;
}

export function useDefaultAccount() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const selectedIdRef = useRef<string | null>(loadStoredId());

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();

    const fetchOnce = async () => {
      try {
        const res = await fetch(`${apiBase()}/portfolio/accounts`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!res.ok) {
          if (mounted.current) {
            setError(`Account list HTTP ${res.status}`);
            setLoading(false);
          }
          return;
        }
        const body = await res.json();
        if (!mounted.current) return;
        if (!body?.ok) {
          setError(body?.error?.message || 'Account list unavailable');
          setLoading(false);
          return;
        }
        const list = (body.data?.accounts || []) as Account[];
        const picked = pickDefault(list, selectedIdRef.current);
        setAccounts(list);
        setAccount(picked);
        if (picked) storeId(picked.id);
        setError(null);
        setLoading(false);
      } catch (err: any) {
        if (!mounted.current || err?.name === 'AbortError') return;
        setError(err?.message || 'Network error');
        setLoading(false);
      }
    };

    fetchOnce();
    const interval = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        fetchOnce();
      }
    }, POLL_MS);

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { accountId?: string } | undefined;
      if (!detail?.accountId) return;
      selectedIdRef.current = detail.accountId;
      storeId(detail.accountId);
      // Re-pick from current list
      setAccount((cur) => {
        const list = (cur && [cur]) || [];
        // Use the latest accounts ref via state setter trick
        return cur;
      });
      // Force a reload
      fetchOnce();
    };
    window.addEventListener('adversyn:select-account', handler as EventListener);

    return () => {
      mounted.current = false;
      controller.abort();
      clearInterval(interval);
      window.removeEventListener('adversyn:select-account', handler as EventListener);
    };
  }, []);

  return { account, accounts, loading, error };
}

export function indexTileFromBars(bars: { c: number; o: number }[]) {
  if (!bars || bars.length === 0) return { value: null as number | null, change: null as number | null };
  const close = bars[bars.length - 1]?.c;
  const open = bars[0]?.o;
  if (close == null || open == null) return { value: null, change: null };
  return { value: close, change: ((close - open) / open) * 100 };
}

export default useDefaultAccount;
